import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguages } from '../../hooks/useLanguages';
import { useCatalog } from '../../hooks/useCatalog';
import { useAuth } from '../../context/AuthContext';
import { useAppConfig } from '../../hooks/useAppConfig';
import { loadVocabularyCatalog, loadVocabularyTopic } from '../../services/vocabularyV2';

function entryText(entry) {
  if (entry && typeof entry === 'object' && !Array.isArray(entry)) return entry.text == null ? '—' : String(entry.text);
  if (Array.isArray(entry)) return entry[0] == null ? '—' : String(entry[0]);
  if (entry == null) return '—';
  return String(entry);
}

function entryImage(entry) {
  if (entry && typeof entry === 'object' && !Array.isArray(entry)) return entry.image || entry.imageName || '';
  if (!Array.isArray(entry)) return '';
  return entry[1] ? String(entry[1]) : '';
}

function entryEmoji(entry) {
  return entry && typeof entry === 'object' && !Array.isArray(entry) ? String(entry.emoji || '') : '';
}

export default function VocabularyPage() {
  const { t } = useTranslation();
  const { languages } = useLanguages();
  const { catalog } = useCatalog();
  const { languagePrefs } = useAuth();
  const config = useAppConfig(languagePrefs?.nativeLanguage);
  const langsWithoutArticles = new Set(config.langsWithoutArticles);

  const TABS = [
    { key: 'thematic',  label: t('vocab.thematic'),   icon: '🗂️', desc: t('vocab.thematicDesc')   },
    { key: 'wordclass', label: t('vocab.wordClasses'), icon: '📝', desc: t('vocab.wordClassesDesc') },
  ];

  const vocabLangs = languages.filter(
    (l) => catalog?.[l.code]?.features?.includes('vocabulary')
  );

  const [indexData, setIndexData] = useState(null);
  const [indexLoading, setIndexLoading] = useState(true);
  const [indexError, setIndexError] = useState(false);
  const [indexRetry, setIndexRetry] = useState(0);
  const [activeTab, setActiveTab] = useState('thematic');
  const [category, setCategory] = useState(null);
  const [nativeCode, setNativeCode] = useState('');
  const [learnCode, setLearnCode] = useState('');

  const [vocabNative, setVocabNative] = useState(null);
  const [vocabLearn, setVocabLearn] = useState(null);
  const [vocabLoading, setVocabLoading] = useState(false);
  const [vocabError, setVocabError] = useState(null);

  const [viewMode, setViewMode] = useState('cards');
  const [showPronunciation, setShowPronunciation] = useState(true);

  const synthRef = useRef(
    typeof window !== 'undefined' && 'speechSynthesis' in window ? window.speechSynthesis : null
  );

  useEffect(() => {
    if (vocabLangs.length === 0 || nativeCode) return;
    const prefNative = languagePrefs?.nativeLanguage;
    const prefTarget = languagePrefs?.targetLanguage;
    const validNative = vocabLangs.find((l) => l.code === prefNative)?.code;
    const validTarget = vocabLangs.find((l) => l.code === prefTarget)?.code;
    setNativeCode(validNative || vocabLangs[1]?.code || vocabLangs[0].code);
    setLearnCode(validTarget || vocabLangs[0].code);
  }, [vocabLangs, nativeCode, languagePrefs]);

  useEffect(() => {
    let active = true;
    setIndexLoading(true);
    setIndexError(false);
    setIndexData(null);
    setCategory(null);
    loadVocabularyCatalog()
      .then((json) => {
        if (!active) return;
        setIndexData(json);
        const first = json?.thematic?.[0];
        if (first) setCategory(first.id);
      })
      .catch(() => {
        if (active) setIndexError(true);
      })
      .finally(() => {
        if (active) setIndexLoading(false);
      });
    return () => { active = false; };
  }, [indexRetry]);

  useEffect(() => {
    if (!indexData) return;
    const first = indexData[activeTab]?.[0];
    if (first) setCategory(first.id);
  }, [activeTab, indexData]);

  useEffect(() => {
    if (!category || !nativeCode || !learnCode) return;
    setVocabLoading(true);
    setVocabError(null);
    setVocabNative(null);
    setVocabLearn(null);

    let cancelled = false;
    Promise.allSettled([
      loadVocabularyTopic(activeTab, nativeCode, category),
      loadVocabularyTopic(activeTab, learnCode, category),
    ]).then(([rN, rL]) => {
      if (cancelled) return;
      if (rN.status === 'fulfilled') setVocabNative(rN.value);
      if (rL.status === 'fulfilled') setVocabLearn(rL.value);
      const errors = [];
      if (rN.status === 'rejected') errors.push(rN.reason.message);
      if (rL.status === 'rejected') errors.push(rL.reason.message);
      if (errors.length) setVocabError(errors.join(' · '));
      setVocabLoading(false);
    });
    return () => { cancelled = true; };
  }, [category, nativeCode, learnCode, activeTab]);

  function getLang(code) {
    return languages.find((l) => l.code === code) || { code, label: code, tts: '' };
  }

  function getOrderedKeys(obj) {
    if (!obj?.content) return [];
    return Object.keys(obj.content);
  }

  function getSortedUnionKeys() {
    const keys = new Set([...getOrderedKeys(vocabNative), ...getOrderedKeys(vocabLearn)]);
    return Array.from(keys);
  }

  function swapLanguages() {
    const a = nativeCode;
    setNativeCode(learnCode);
    setLearnCode(a);
  }

  function speakText(ttsCode, text) {
    if (!synthRef.current || !text) return;
    try {
      synthRef.current.cancel();
      const ut = new SpeechSynthesisUtterance(text);
      ut.lang = ttsCode || '';
      ut.rate = 0.95;
      ut.onerror = () => {};
      synthRef.current.speak(ut);
    } catch (e) {}
  }

  const SpeakerIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M9.383 3.076A1 1 0 0110 4v12a1 1 0 01-1.617.816L4.766 14H3a1 1 0 01-1-1V7a1 1 0 011-1h1.766l3.617-2.816a1 1 0 011.017-.108zM14.657 2.929a1 1 0 011.414 0A9.972 9.972 0 0119 10a9.972 9.972 0 01-2.929 7.071 1 1 0 01-1.414-1.414A7.971 7.971 0 0017 10c0-2.21-.894-4.208-2.343-5.657a1 1 0 010-1.414zm-2.829 2.828a1 1 0 011.415 0A5.983 5.983 0 0115 10a5.984 5.984 0 01-1.757 4.243 1 1 0 01-1.415-1.415A3.984 3.984 0 0013 10a3.983 3.983 0 00-1.172-2.828 1 1 0 010-1.415z" clipRule="evenodd" />
    </svg>
  );

  const isWordClass = activeTab === 'wordclass';
  const nativeLangObj = getLang(nativeCode);
  const learnLangObj  = getLang(learnCode);

  function renderWordClassList() {
    const rows = getSortedUnionKeys();
    if (!rows.length) return (
      <p className="text-center text-slate-500 py-10 text-sm italic">{t('vocab.noWords')}</p>
    );
    return (
      <div className="divide-y divide-slate-100">
        {rows.map((k) => {
          const nativeItem = vocabNative?.content?.[k];
          const learnItem  = vocabLearn?.content?.[k];
          const nativeText = entryText(nativeItem);
          const learnText  = entryText(learnItem);
          return (
            <div key={k} className="flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors">
              <span className="w-6 shrink-0 text-center text-xs font-black text-slate-300">{k}</span>
              <div className="flex-1 flex items-center gap-2">
                <span className="text-sm font-semibold text-slate-800">{nativeText}</span>
                {showPronunciation && nativeItem && (
                  <button onClick={() => speakText(nativeLangObj.tts, nativeText)}
                    className="p-1 rounded-full bg-indigo-50 text-indigo-500 hover:bg-indigo-100 transition-colors shrink-0">
                    <SpeakerIcon />
                  </button>
                )}
              </div>
              <svg className="h-4 w-4 shrink-0 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
              <div className="flex-1 flex items-center gap-2">
                <span dir={learnLangObj.dir || 'ltr'} className="text-sm font-bold text-indigo-700">{learnText}</span>
                {showPronunciation && learnItem && (
                  <button onClick={() => speakText(learnLangObj.tts, learnText)}
                    className="p-1 rounded-full bg-indigo-50 text-indigo-500 hover:bg-indigo-100 transition-colors shrink-0">
                    <SpeakerIcon />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  function renderTable() {
    const rows = getSortedUnionKeys();
    if (!rows.length) return <p className="text-slate-500 text-center py-8 text-sm">{t('vocab.noWords')}</p>;
    return (
      <div className="overflow-auto rounded-xl border border-slate-200">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-black uppercase tracking-widest text-slate-400">
                {vocabNative?.label || nativeLangObj.label}
              </th>
              <th className="px-6 py-3 text-left text-xs font-black uppercase tracking-widest text-slate-400">
                {vocabLearn?.label || learnLangObj.label}
              </th>
              {!isWordClass && (
                <th className="px-6 py-3 text-left text-xs font-black uppercase tracking-widest text-slate-400">{t('vocab.image')}</th>
              )}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-slate-100">
            {rows.map((k) => {
              const nativeItem = vocabNative?.content?.[k];
              const learnItem  = vocabLearn?.content?.[k];
              const nativeText = entryText(nativeItem);
              const learnText  = entryText(learnItem);
              const imageFile  = (!isWordClass && (entryImage(learnItem) || entryImage(nativeItem))) || '';
              const imgSrc     = imageFile ? `/images/vocabulary/${category}/${imageFile}` : null;
              const emoji      = entryEmoji(learnItem) || entryEmoji(nativeItem);
              return (
                <tr key={k} className="hover:bg-slate-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-800">{nativeText}</span>
                      {showPronunciation && nativeItem && (
                        <button onClick={() => speakText(nativeLangObj.tts, nativeText)}
                          className="p-1.5 rounded-full bg-indigo-50 text-indigo-500 hover:bg-indigo-100 transition-colors">
                          <SpeakerIcon />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <span dir={learnLangObj.dir || 'ltr'} className="text-sm font-semibold text-indigo-700">{learnText}</span>
                      {showPronunciation && learnItem && (
                        <button onClick={() => speakText(learnLangObj.tts, learnText)}
                          className="p-1.5 rounded-full bg-indigo-50 text-indigo-500 hover:bg-indigo-100 transition-colors">
                          <SpeakerIcon />
                        </button>
                      )}
                    </div>
                  </td>
                  {!isWordClass && (
                    <td className="px-6 py-4 whitespace-nowrap">
                      {imgSrc ? (
                        <img src={imgSrc} alt={learnText || nativeText}
                          onError={(e) => { e.currentTarget.style.display = 'none'; }}
                          className="w-16 h-16 object-cover rounded-lg shadow-sm" />
                      ) : <span className="text-3xl" aria-hidden="true">{emoji || '—'}</span>}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  function renderCards() {
    const rows = getSortedUnionKeys();
    if (!rows.length) return <p className="text-slate-500 text-center py-8 text-sm">{t('vocab.noWords')}</p>;
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {rows.map((k) => {
          const nativeItem  = vocabNative?.content?.[k];
          const learnItem   = vocabLearn?.content?.[k];
          const nativeLabel = entryText(nativeItem);
          const learnLabel  = entryText(learnItem);
          const imageFile   = (!isWordClass && (entryImage(learnItem) || entryImage(nativeItem))) || '';
          const imgSrc      = imageFile ? `/images/vocabulary/${category}/${imageFile}` : null;
          const emoji       = entryEmoji(learnItem) || entryEmoji(nativeItem);
          return (
            <div key={k} className="bg-white rounded-2xl shadow-sm overflow-hidden border border-slate-100 hover:shadow-md transition-all duration-200">
              {!isWordClass && (
                <div className="h-36 bg-slate-100 flex items-center justify-center overflow-hidden">
                  {imgSrc ? (
                    <img src={imgSrc} alt={learnLabel || nativeLabel}
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      className="object-cover w-full h-full" />
                  ) : emoji ? (
                    <span className="text-6xl" aria-hidden="true">{emoji}</span>
                  ) : (
                    <div className="text-slate-300 flex flex-col items-center gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-9 w-9" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                  )}
                </div>
              )}
              <div className={`p-4 ${isWordClass ? 'py-5' : ''}`}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                    {nativeLangObj.label}
                  </span>
                  {showPronunciation && nativeItem && (
                    <button onClick={() => speakText(nativeLangObj.tts, nativeLabel)}
                      className="p-1.5 rounded-full bg-indigo-50 text-indigo-500 hover:bg-indigo-100 transition-colors">
                      <SpeakerIcon />
                    </button>
                  )}
                </div>
                <p className="text-base font-semibold text-slate-800 mb-3">{nativeLabel}</p>
                <div className="border-t border-slate-100 pt-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-400">
                      {learnLangObj.label}
                    </span>
                    {showPronunciation && learnItem && (
                      <button onClick={() => speakText(learnLangObj.tts, learnLabel)}
                        className="p-1.5 rounded-full bg-indigo-50 text-indigo-500 hover:bg-indigo-100 transition-colors">
                        <SpeakerIcon />
                      </button>
                    )}
                  </div>
                  <p dir={learnLangObj.dir || 'ltr'} className="text-base font-bold text-indigo-700">{learnLabel}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const currentCats = indexData?.[activeTab] || [];
  const activeCatMeta = currentCats.find((c) => c.id === category);

  if (indexLoading || indexError) {
    return (
      <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at 20% 0%, #ede9fe 0%, #f8fafc 55%, #ecfdf5 100%)' }}>
        <div className="mx-auto max-w-6xl space-y-8 px-4 py-10">
          <div>
            <p className="mb-1 text-xs font-black uppercase tracking-widest text-violet-600">{t('tools.vocabulary')}</p>
            <h1 className="text-2xl font-black text-slate-900">{t('vocab.title')}</h1>
            <p className="mt-1 text-sm text-slate-500">{t('vocab.subtitle')}</p>
          </div>
          {indexLoading ? (
            <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="h-5 w-48 animate-pulse rounded bg-slate-100" />
              <div className="h-12 animate-pulse rounded-xl bg-slate-100" />
              <div className="h-32 animate-pulse rounded-xl bg-slate-100" />
            </div>
          ) : (
            <div
              role="alert"
              className="rounded-2xl border border-rose-200 bg-white p-6 text-center shadow-sm"
            >
              <p className="font-black text-slate-800">{t('learn.failed')}</p>
              <button
                type="button"
                onClick={() => setIndexRetry((value) => value + 1)}
                className="mt-4 rounded-xl bg-violet-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-violet-700"
              >
                {t('common.retry')}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'radial-gradient(ellipse at 20% 0%, #ede9fe 0%, #f8fafc 55%, #ecfdf5 100%)' }}>
      <div className="mx-auto max-w-6xl px-4 py-10 space-y-8">

        {/* Header */}
        <div>
          <p className="text-xs font-black uppercase tracking-widest text-violet-600 mb-1">{t('tools.vocabulary')}</p>
          <h1 className="text-2xl font-black text-slate-900">{t('vocab.title')}</h1>
          <p className="mt-1 text-sm text-slate-500">{t('vocab.subtitle')}</p>
        </div>

        {/* Language + controls bar */}
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <div className="flex flex-wrap items-center gap-3 flex-1">
              <span className="text-sm font-semibold text-slate-600 shrink-0">{t('vocab.iKnow')}</span>
              <select value={nativeCode} onChange={(e) => setNativeCode(e.target.value)}
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-400">
                {vocabLangs.map((l) => (
                  <option key={l.code} value={l.code}>{`${l.label}${l.nativeLabel && l.nativeLabel !== l.label ? ` — ${l.nativeLabel}` : ''}`}</option>
                ))}
              </select>
              <button onClick={swapLanguages}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100 transition-colors shrink-0">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              </button>
              <span className="text-sm font-semibold text-slate-600 shrink-0">{t('vocab.learning')}</span>
              <select value={learnCode} onChange={(e) => setLearnCode(e.target.value)}
                className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 focus:outline-none focus:ring-2 focus:ring-violet-400">
                {vocabLangs.map((l) => (
                  <option key={l.code} value={l.code}>{`${l.label}${l.nativeLabel && l.nativeLabel !== l.label ? ` — ${l.nativeLabel}` : ''}`}</option>
                ))}
              </select>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <div className="flex rounded-xl border border-slate-200 bg-slate-50 p-1 gap-1">
                <button onClick={() => setViewMode('cards')}
                  className={`rounded-lg p-1.5 transition ${viewMode === 'cards' ? 'bg-white shadow-sm text-violet-600' : 'text-slate-400 hover:text-slate-600'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button onClick={() => setViewMode('table')}
                  className={`rounded-lg p-1.5 transition ${viewMode === 'table' ? 'bg-white shadow-sm text-violet-600' : 'text-slate-400 hover:text-slate-600'}`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
              </div>
              <button onClick={() => setShowPronunciation((p) => !p)}
                className={`flex items-center gap-1.5 rounded-xl px-3 py-2 text-xs font-bold transition ${showPronunciation ? 'bg-emerald-100 text-emerald-700' : 'border border-slate-200 bg-white text-slate-500'}`}>
                <SpeakerIcon />
                {showPronunciation ? 'On' : 'Off'}
              </button>
            </div>
          </div>
        </div>

        {/* Type tabs */}
        <div>
          <div className="flex gap-2">
            {TABS.map((tab) => (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex shrink-0 items-center gap-2 rounded-xl px-5 py-3 text-sm font-bold transition ${
                  activeTab === tab.key
                    ? 'bg-violet-600 text-white shadow-md shadow-violet-400/30'
                    : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                }`}>
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-slate-400 pl-1">
            {TABS.find((t) => t.key === activeTab)?.desc}
          </p>
        </div>

        {/* Category pills */}
        <div className="flex flex-wrap gap-2">
          {currentCats.map((cat) => (
            <button key={cat.id} onClick={() => setCategory(cat.id)}
              className={`flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-bold transition ${
                cat.id === category
                  ? 'bg-violet-600 text-white shadow-md shadow-violet-400/30'
                  : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
              }`}>
              <span>{cat.icon}</span>
              <span>{cat.label}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
            <div>
              <h2 className="text-lg font-black text-slate-900">
                {activeCatMeta?.icon} {activeCatMeta?.label || category}
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {nativeLangObj.label} → {learnLangObj.label}
              </p>
            </div>
            {vocabLoading && (
              <div className="flex items-center gap-2 text-violet-500">
                <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-violet-500" />
                <span className="text-xs font-semibold">{t('common.loading')}</span>
              </div>
            )}
          </div>

          {vocabError && category !== 'articles' && (
            <div className="mx-5 mt-4 rounded-xl bg-red-50 px-4 py-3 text-sm text-red-600">{vocabError}</div>
          )}

          {category === 'articles' && (
            <div className="mx-5 mt-4 space-y-2">
              {langsWithoutArticles.has(nativeCode) && (
                <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3">
                  <span className="mt-0.5 text-base">ℹ️</span>
                  <p className="text-sm text-blue-800">
                    <span className="font-bold">{nativeLangObj.label}</span>{' '}
                    {t('vocab.noArticles', { lang: nativeLangObj.label })}
                  </p>
                </div>
              )}
              {langsWithoutArticles.has(learnCode) && (
                <div className="flex items-start gap-3 rounded-xl border border-amber-100 bg-amber-50 px-4 py-3">
                  <span className="mt-0.5 text-base">ℹ️</span>
                  <p className="text-sm text-amber-800">
                    <span className="font-bold">{learnLangObj.label}</span>{' '}
                    {t('vocab.noArticles', { lang: learnLangObj.label })}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className={isWordClass && viewMode === 'cards' ? '' : 'p-5'}>
            {isWordClass
              ? (viewMode === 'table' ? <div className="p-5">{renderTable()}</div> : renderWordClassList())
              : (viewMode === 'table' ? renderTable() : renderCards())
            }
          </div>
        </div>

      </div>
    </div>
  );
}
